import api from './api';

const categoryService = {
  // Get all categories (flat list)
  getCategories: async () => {
    const response = await api.get('/api/admin/categories');
    return response.data;
  },

  // Get single category by ID
  getCategory: async (id) => {
    const response = await api.get(`/api/admin/categories/${id}`);
    return response.data;
  },

  // Create new category
  createCategory: async (data) => {
    const response = await api.post('/api/admin/categories', data);
    return response.data;
  },

  // Update existing category
  updateCategory: async (id, data) => {
    const response = await api.put(`/api/admin/categories/${id}`, data);
    return response.data;
  },

  // Delete category
  deleteCategory: async (id) => {
    const response = await api.delete(`/api/admin/categories/${id}`);
    return response.data;
  },

  // Get products by category
  getProductsByCategory: async (categoryId, params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) queryParams.append('page', params.page);
    if (params.size) queryParams.append('size', params.size);

    queryParams.append('category', categoryId);

    const queryString = queryParams.toString();
    const url = `/api/admin/products${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);
    return response.data;
  },

  // Reorder categories
  reorderCategories: async (categoryOrders) => {
    // categoryOrders should be array of {id, displayOrder}
    const response = await api.put('/api/admin/categories/reorder', { categoryOrders });
    return response.data;
  },

  // Helper: Build tree structure from flat list
  buildTree: (categories) => {
    const map = {};
    const roots = [];

    // First pass: create map of all categories
    categories.forEach(cat => {
      map[cat.id] = { ...cat, children: [] };
    });

    // Second pass: build tree structure
    categories.forEach(cat => {
      if (cat.parent && cat.parent.id && map[cat.parent.id]) {
        // Has parent - add to parent's children
        map[cat.parent.id].children.push(map[cat.id]);
      } else {
        // No parent - this is a root category
        roots.push(map[cat.id]);
      }
    });

    // Sort by displayOrder at each level
    const sortByDisplayOrder = (nodes) => {
      nodes.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          sortByDisplayOrder(node.children);
        }
      });
    };

    sortByDisplayOrder(roots);

    return roots;
  },

  // Helper: Check if a category is descendant of another
  isDescendant: (categories, possibleDescendantId, ancestorId) => {
    if (!possibleDescendantId || !ancestorId) return false;
    if (possibleDescendantId === ancestorId) return true;

    const category = categories.find(c => c.id === possibleDescendantId);
    if (!category || !category.parent) return false;

    return categoryService.isDescendant(categories, category.parent.id, ancestorId);
  },

  // Helper: Get all descendants of a category
  getDescendants: (categories, categoryId) => {
    const descendants = [];

    const findChildren = (parentId) => {
      const children = categories.filter(c => c.parent?.id === parentId);
      children.forEach(child => {
        descendants.push(child);
        findChildren(child.id);
      });
    };

    findChildren(categoryId);
    return descendants;
  },

  // Helper: Validate category data before save
  validateCategory: (categoryData, allCategories, currentCategoryId = null) => {
    const errors = {};

    // Required fields
    if (!categoryData.name || !categoryData.name.trim()) {
      errors.name = 'Category name is required';
    }

    if (!categoryData.slug || !categoryData.slug.trim()) {
      errors.slug = 'Slug is required';
    }

    // Slug format
    if (categoryData.slug && !/^[a-z0-9-]+$/.test(categoryData.slug)) {
      errors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }

    // Check for duplicate slug
    const duplicateSlug = allCategories.find(
      c => c.slug === categoryData.slug && c.id !== currentCategoryId
    );
    if (duplicateSlug) {
      errors.slug = 'A category with this slug already exists';
    }

    // Description validation (required, 10-500 chars)
    if (!categoryData.description || !categoryData.description.trim()) {
      errors.description = 'Description is required';
    } else if (categoryData.description.trim().length < 10) {
      errors.description = 'Description must be at least 10 characters';
    } else if (categoryData.description.trim().length > 500) {
      errors.description = 'Description must be less than 500 characters';
    }

    // Display order must be non-negative
    if (categoryData.displayOrder !== undefined && categoryData.displayOrder < 0) {
      errors.displayOrder = 'Display order cannot be negative';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },
};

export default categoryService;
